<script setup lang="ts">
import { SIDEBAR_IDS } from '@design-system/data/sidebarIds'
import type {
  PdpVirtualMirrorModalProps,
  VirtualMirrorProductData,
  ScreenshotReturns,
} from '@design-system/components/Pdp/EcommerceBlock/PdpVirtualMirrorModal.props'
import type { ColorOption } from '@design-system/components/Pdp/PdpColorSelector.props'

const props = defineProps<PdpVirtualMirrorModalProps>()

const { closeDialog, isDialogOpen } = useDialog(
  SIDEBAR_IDS.pdpVirtualMirrorModal
)

const disabled = useState<boolean | undefined>('disabledMobileAddToCart')
const selectedExtOutOfStock = useState<boolean | undefined>(
  'selectedExtOutOfStockMobile'
)
const onlyStore = useState<boolean | undefined>('onlyStoreMobile')
const virtualMirrorCookie = useCookie('virtual-mirror-accepted')
const { formatPriceByLocale } = useFormatPrice()
const { currentCurrency } = useCurrency()
const showActionsBar = ref(false)

const activeSku = ref<string | null>(null)
const changedProduct = ref<VirtualMirrorProductData | null>(null)

const getCurrentProduct = (
  changedProduct?: VirtualMirrorProductData | null
) => {
  if (changedProduct) return changedProduct
  return props.productDetails
}

const mountVirtualMirror = async (option: ColorOption) => {
  if (!option.sku) return
  const product = await props.getProductVirtualMirror(option.sku)
  if (!product) return
  changedProduct.value = product
  const upc = product?.upc ?? null
  if (!upc || !props.targetId) return
  activeSku.value = option.sku
  await nextTick()
  await props.renderMirror({
    target: props.targetId,
    glasses: [{ upc }],
  })
  if (!showActionsBar.value) showActionsBar.value = true
}

const startVirtualMirror = () => {
  virtualMirrorCookie.value = 'true'
  mountVirtualMirror(props.productDetails.colorOptions[0])
}

const closeModal = () => {
  closeDialog(SIDEBAR_IDS.pdpVirtualMirrorModal)
}

const screenshot = ref<ScreenshotReturns | undefined>()
const handleScreenshot = async () => {
  if (!props.getScreenshot) return
  const result = await props.getScreenshot()
  if (!result?.imgURI) return
  screenshot.value = result
}

const closeScreenshot = () => {
  screenshot.value = undefined
}

watch(
  () => isDialogOpen.value,
  async open => {
    if (!open && props.targetId) {
      await props.closeMirror({ target: props.targetId })
      showActionsBar.value = false
    } else if (
      open &&
      virtualMirrorCookie.value &&
      props.productDetails.colorOptions.length > 0
    ) {
      await mountVirtualMirror(props.productDetails.colorOptions[0])
    }
  }
)
</script>

<template>
  <OrganismsModal
    :id="SIDEBAR_IDS.pdpVirtualMirrorModal"
    hide-close-button
    with-overlay
    class="!z-[60]"
    custom-dialog-panel-class="mx-auto max-w-[768px] overflow-hidden"
  >
    <template #body>
      <div class="relative h-dvh w-full">
        <!-- Privacy agreement -->
        <div
          v-if="!virtualMirrorCookie"
          class="flex h-full flex-col justify-center gap-6 p-6 md:justify-between"
        >
          <div
            class="space-between flex h-[calc(100dvh-80px)] flex-col gap-6 overflow-y-auto pb-[140px] md:pb-[80px]"
          >
            <div
              class="from-background-secondary pointer-events-none absolute inset-x-0 bottom-[130px] z-10 h-12 bg-gradient-to-t to-transparent md:bottom-[80px]"
            ></div>
            <div class="flex items-center justify-between">
              <h3 class="text-medium-5 md:text-medium-4 text-text-primary">
                {{ $ts('pdp.virtualMirror.privacyAgreement.title') }}
              </h3>
              <button
                class="h-8 w-8"
                :aria-label="$ts('close')"
                @click="closeModal"
              >
                <DefaultIconsClose
                  aria-hidden="true"
                  class="text-text-primary text-shadow-md h-full w-full"
                />
              </button>
            </div>

            <div
              class="text-book-6 text-text-primary [&_a]:text-inherit"
              v-html="$ts('pdp.virtualMirror.privacyAgreement.text')"
            />
          </div>

          <div
            class="bg-neutral-white absolute bottom-0 left-0 right-0 z-10 flex h-[140px] flex-col-reverse items-center gap-2 p-4 md:h-[80px] md:flex-row md:gap-4"
          >
            <AtomsCta
              :icon-show-default="false"
              class="w-full md:flex-1"
              anatomy="secondary"
              @click="closeModal"
              ><template #label>{{
                $ts('pdp.virtualMirror.privacyAgreement.reject')
              }}</template></AtomsCta
            >
            <AtomsCta
              :icon-show-default="false"
              class="w-full md:flex-1"
              anatomy="primary"
              @click="startVirtualMirror"
              ><template #label>{{
                $ts('pdp.virtualMirror.privacyAgreement.accept')
              }}</template></AtomsCta
            >
          </div>
        </div>
        <!-- Virtual Mirror -->
        <div v-if="virtualMirrorCookie" class="relative h-full w-full">
          <div
            class="pointer-events-none absolute left-0 top-0 z-10 h-16 w-full bg-gradient-to-b from-[rgba(0,0,0,0.65)] to-transparent"
          />
          <div
            :id="targetId"
            class="bg-primitives-off-white relative h-full w-full"
            aria-label="Virtual try-on mirror"
          />
          <div v-if="showActionsBar" class="absolute bottom-0 left-0 w-full">
            <!-- Color selector -->
            <div
              v-if="productDetails.colorOptions.length > 1"
              class="flex justify-center gap-2 pb-3"
            >
              <button
                v-for="(option, i) in productDetails.colorOptions"
                :key="i"
                type="button"
                class="shrink-0 overflow-hidden border-2 transition-all"
                :class="
                  option.sku === activeSku
                    ? 'border-primitives-black shadow-lg'
                    : 'border-transparent opacity-70'
                "
                :aria-label="option.color"
                @click="mountVirtualMirror(option)"
              >
                <NuxtPicture
                  v-if="option.thumbnailUrl"
                  provider="cloudinary"
                  :src="option.thumbnailUrl"
                  fit="fill"
                  sizes="60px"
                  class="h-full w-full object-cover"
                />
                <div
                  v-else-if="option.hexColor"
                  class="h-full w-full"
                  :style="{ backgroundColor: `#${option.hexColor}` }"
                />
              </button>
            </div>
            <!-- Actions bar -->
            <div
              class="bg-primitives-white flex h-20 items-center justify-between gap-2 p-4 md:p-6"
            >
              <button class="" @click="handleScreenshot">
                <DefaultIconsCamera
                  aria-label="Take screenshot"
                  class="h-8 w-8 md:h-10 md:w-10"
                />
              </button>
              <div class="flex flex-col items-center max-md:max-w-[30%]">
                <p
                  class="md:text-medium-6 text-medium-7 w-full overflow-hidden text-ellipsis whitespace-nowrap uppercase"
                >
                  {{ getCurrentProduct(changedProduct).title }}
                </p>
                <p class="text-book-6 uppercase">
                  {{
                    formatPriceByLocale(
                      currentCurrency,
                      getCurrentProduct(changedProduct).price
                    )
                  }}
                </p>
              </div>
              <HeaderActionAddToCart
                class="max-w-[40%] list-none [&>button]:!rounded-none [&>button]:!p-2 [&>button]:md:!p-4"
                :disabled="disabled"
                :selected-ext-out-of-stock="selectedExtOutOfStock"
                :only-store="onlyStore"
              />
            </div>
          </div>
          <button
            class="absolute right-4 top-4 z-10 h-10 w-10"
            :aria-label="$ts('close')"
            @click="closeModal"
          >
            <DefaultIconsClose
              aria-hidden="true"
              class="text-primitives-white h-full w-full"
            />
          </button>
          <!-- Screenshot modal -->
          <div
            class="bg-primitives-white absolute bottom-0 left-0 z-10 h-[98%] w-full overflow-hidden rounded-t-2xl p-4 transition-transform duration-300 ease-in-out md:p-6"
            :class="{
              'pointer-events-none translate-y-full': !screenshot,
              'translate-y-0': screenshot,
            }"
          >
            <button
              class="absolute right-4 top-4 z-10 h-10 w-10"
              :aria-label="$ts('close')"
              @click="closeScreenshot"
            >
              <DefaultIconsClose
                aria-hidden="true"
                class="text-text-primary h-full w-full"
              />
            </button>
            <div
              class="flex h-full flex-col items-center pt-12 text-center md:p-12"
            >
              <img
                v-if="screenshot"
                :src="screenshot.imgURI"
                :alt="`virtual-mirror-screenshot-${Date.now()}`"
                class="aspect-[0.79/1] w-full max-w-[300px] object-cover md:max-w-[350px]"
              />
              <a
                v-if="screenshot"
                :href="screenshot.imgURI"
                :download="`virtual-mirror-screenshot-${Date.now()}.png`"
                class="text-medium-5 underline-effect-appear mt-2 flex items-center gap-1"
              >
                <span class="text-effect">
                  {{ $ts('pdp.virtualMirror.screenshot.download') }}
                </span>
                <DefaultIconsDown aria-hidden="true" class="h-6 w-6" />
              </a>
              <div class="mt-8 flex flex-col gap-1">
                <p class="text-book-5 w-full">
                  {{ productDetails.brand }}
                </p>
                <p class="text-medium-5 w-full uppercase">
                  {{ getCurrentProduct(changedProduct).title }}
                </p>
                <p class="text-book-5 w-full">
                  {{ getCurrentProduct(changedProduct).selectedColor }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </OrganismsModal>
</template>
